import { Component, Input, inject, ViewChild, ElementRef, OnInit, EventEmitter, Output, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SearchService } from '@app/shared/services/search.service';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AuthService } from '../../../core/services/auth.service';
import { User, UserRole, UserRoleIdMap } from '../../models/user.model';
import { RecentAlert } from '@app/shared/models/dashboard.model';
import { Router, RouterModule } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDividerModule } from '@angular/material/divider';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { NotificationsService, PortalNotification } from '../../../core/services/notifications.service';
import { RealtimeService } from '../../../core/services/realtime.service';
import { Subscription, interval, merge } from 'rxjs';

type NotificationKind = 'created' | 'comment' | 'status';

interface HeaderNotification {
  id: number;
  title: string;
  message: string;
  timeAgo: string;
  isRead: boolean;
  type: 'serviceRequest';
  kind: NotificationKind;
  serviceRequestId?: number;
  serviceRequestTicket?: string;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatBadgeModule,
    MatDividerModule,
    RouterModule,
    MatCardModule,
    MatListModule,
    MatFormFieldModule,
    TooltipComponent,
    TranslateModule
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() isMobileOrTablet = false;
  @Input() sidenavOpened = false;
  @Output() openSidenavClicked = new EventEmitter<void>();
  @ViewChild('searchInput') searchInputRef?: ElementRef<HTMLInputElement>;
  currentUser: User | null = null;
  recentAlerts: RecentAlert[] = [];
  notifications: HeaderNotification[] = [];
  private readonly viewedNotificationIds = new Set<number>();
  private notificationsUpdatesSubscription?: Subscription;
  // Notifications arrive live over the realtime hub; this slow poll is only a safety net for a
  // signal lost while the connection was down.
  private readonly notificationsFallbackPollingIntervalMs = 5 * 60 * 1000;
  
  private authService = inject(AuthService);
  private router = inject(Router);
  private searchService = inject(SearchService);
  private notificationsService = inject(NotificationsService);
  private realtimeService = inject(RealtimeService);
  private cdr = inject(ChangeDetectorRef, { optional: true });

  get unreadNotificationCount(): number {
    return this.notifications.filter((notification) => !notification.isRead).length;
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchService.setSearch(value);
  }
  ngOnInit(): void {
    this.initializeNotifications();
    this.startNotificationsUpdates();
    this.authService.currentUser$.subscribe(user => {
      this.currentUser = user;
      const roleEntry = Object.entries(UserRoleIdMap).find(([, id]) => id === Number(user?.roleId));
      if (roleEntry) {
        this.currentUser!.role = roleEntry[0] as UserRole;
      }
    });
    this.router.events.subscribe(() => {
      this.searchService.setSearch('');
      if (this.searchInputRef?.nativeElement) {
        this.searchInputRef.nativeElement.value = '';
      }
    });
  }

  ngOnDestroy(): void {
    this.notificationsUpdatesSubscription?.unsubscribe();
  }

  private readonly notificationIconMap: Record<NotificationKind, string> = {
    created: 'confirmation_number',
    comment: 'chat_bubble_outline',
    status: 'sync_alt',
  };

  private resolveNotificationKind(title: string): NotificationKind {
    const normalized = title.toLowerCase();
    if (normalized.includes('comment') || normalized.includes('reply')) {
      return 'comment';
    }
    if (normalized.includes('status') || normalized.includes('reopened')) {
      return 'status';
    }
    return 'created';
  }

  getNotificationIcon(kind: NotificationKind): string {
    return this.notificationIconMap[kind];
  }

  private initializeNotifications(): void {
    this.notificationsService.getMine().subscribe((apiNotifications: PortalNotification[]) => {
      this.notifications = apiNotifications.map((notification) => ({
        id: notification.id,
        title: notification.title,
        message: notification.message,
        timeAgo: notification.timeAgo,
        isRead: notification.isViewed || this.viewedNotificationIds.has(notification.id),
        type: 'serviceRequest',
        kind: this.resolveNotificationKind(notification.title),
        serviceRequestId: notification.serviceRequestId,
        serviceRequestTicket: notification.serviceRequestTicket,
      }));

      this.notifications
        .filter((notification) => notification.isRead)
        .forEach((notification) => this.viewedNotificationIds.add(notification.id));

      this.cdr?.detectChanges();
    });
  }

  private startNotificationsUpdates(): void {
    this.notificationsUpdatesSubscription?.unsubscribe();
    this.notificationsUpdatesSubscription = merge(
      this.realtimeService.notificationsChanged(),
      interval(this.notificationsFallbackPollingIntervalMs),
    ).subscribe(() => {
      this.initializeNotifications();
    });
  }

  onNotificationClick(notification: HeaderNotification): void {
    if (!notification.isRead && !this.viewedNotificationIds.has(notification.id)) {
      notification.isRead = true;
      this.viewedNotificationIds.add(notification.id);
      this.notificationsService.markViewed(notification.id).subscribe((marked) => {
        if (!marked) {
          this.viewedNotificationIds.delete(notification.id);
          notification.isRead = false;
        }

        this.cdr?.detectChanges();
      });
    }

    if (notification.type === 'serviceRequest' && notification.serviceRequestId) {
      this.router.navigate(['/serviceRequests/details', notification.serviceRequestId]);
    }
  }

  openSidenav() {
    this.openSidenavClicked.emit();
  }

  logout(): void {
    this.authService.logout();
  }


}